import { computed, Ref, ref } from 'vue'
import useSubscribersMap from '@/composables/subscribers/subscribersMap'
import useSubscribersApi from '@/composables/subscribers/subscribersApi'
import useCurrentUser, { useProfileShow } from '@/composables/user/user'
import useConfiguration from '@/composables/configuration/configuration'
import { StatusCodes } from '@/composables/api'
import { UserNotification } from '@/model/data-object/UserNotification'
import { ApiTimeout } from '@/composables/apiTimeout'
import { TOOLTIP_COMPONENTS } from '@/composables/post/postTooltip'
import useAnalyticsManager, {
  ANALYTICS_EVENT_POST_SUBSCRIBE,
  ANALYTICS_EVENT_POST_USER_SUBSCRIBE,
} from '@/composables/analytics/analyticsManager'
import { Post } from '@/model/data-object/Post'
import useProfileMenu, { ProfileMenu } from '@/composables/profile/profileMenu'
import { Subscriber } from '@/model/data-object/Subscriber'

const TIMEOUT_DOMAIN_SUBSCRIBE_POST = 'forum_widget_post_subscribe_post_'
const TIMEOUT_DOMAIN_SUBSCRIBE_TOPIC = 'forum_widget_post_subscribe_topic_'

const totalCount: Ref<number> = ref<number>(0)

const { activePage } = useProfileMenu()

const currentUserSubscription: Ref<UserNotification | undefined> = ref<
  UserNotification | undefined
>()

function loadFollowers() {
  const { fetchMyFollowers, fetchUserFollowers } = useSubscribersApi()
  const { isShowingCurrentUser } = useProfileShow()
  const { configuration } = useConfiguration()
  const { subscribers } = useSubscribersMap()

  return isShowingCurrentUser.value
    ? fetchMyFollowers(subscribers.value.length)
    : fetchUserFollowers(configuration.widget.userId, subscribers.value.length)
}

function loadSubscribedUsers() {
  const { fetchMySubscribers, fetchUserSubscribedUsers } = useSubscribersApi()
  const { isShowingCurrentUser } = useProfileShow()
  const { configuration } = useConfiguration()
  const { subscribers } = useSubscribersMap()

  return isShowingCurrentUser.value
    ? fetchMySubscribers(subscribers.value.length)
    : fetchUserSubscribedUsers(
        configuration.widget.userId,
        subscribers.value.length
      )
}

export default function useSubscriberManager() {
  const {
    subscribers,
    addSubscribers,
    removeSubscriber,
    clearMap,
  } = useSubscribersMap()
  const {
    subscribePost: subscribePostApi,
    isUserSubscribed: isUserSubscribedApi,
    subscribeUser: subscribeUserApi,
    removeSubscription: removeSubscriptionApi,
  } = useSubscribersApi()
  const loading = ref(false)
  const removing = ref(false)
  const isSubscribedInitialized = ref(false)

  const remaining = computed(() => {
    return totalCount.value - subscribers.value.length
  })

  const clearList = () => {
    totalCount.value = 0
    clearMap()
  }

  const fetchIsUserSubscribed = async (userId: number) => {
    const { request, response } = isUserSubscribedApi(userId)
    await request()
    isSubscribedInitialized.value = true
    if (response.value) {
      currentUserSubscription.value = response.value
    }
  }

  const subscribeUser = async (userId: number) => {
    const { request } = subscribeUserApi(userId)
    await request()
  }

  const subscribePost = async (postId: number) => {
    const { request } = subscribePostApi(postId)
    await request()
  }

  const removeSubscription = async (postId: number) => {
    const { request } = removeSubscriptionApi(postId)
    removing.value = true
    await request()
    removing.value = false
    removeSubscriber(postId)
    totalCount.value--
  }

  const loadNext = async () => {
    const { request, response, count } =
      activePage.value === ProfileMenu.FOLLOWERS
        ? loadFollowers()
        : loadSubscribedUsers()
    loading.value = true
    await request()
    loading.value = false

    if (response.value !== null) {
      addSubscribers(response.value as Subscriber[])
      totalCount.value = count.value
    }
  }

  return {
    isSubscribedInitialized,
    subscribers,
    currentUserSubscription,
    remaining,
    loading,
    removing,
    loadNext,
    clearList,
    subscribePost,
    subscribeUser,
    removeSubscription,
    fetchIsUserSubscribed,
  }
}

export function useSubscribePost(
  post: Post,
  currentComponent: Ref<string | undefined>,
  alreadyPerformed: Ref<boolean>
) {
  const { subscribePost: subscribePostApi } = useSubscribersApi()
  const { propagateAnalyticsEvent } = useAnalyticsManager()
  const { loggedIn } = useCurrentUser()
  const { request, loading: isSubscribing, statusCode } = subscribePostApi(
    post.id,
    new ApiTimeout(TIMEOUT_DOMAIN_SUBSCRIBE_POST + post.id)
  )
  const subscribePost = async () => {
    if (!loggedIn.value) {
      if (currentComponent.value === TOOLTIP_COMPONENTS.POST_SUBSCRIBE_LOGIN) {
        currentComponent.value = undefined

        return
      }
      currentComponent.value = TOOLTIP_COMPONENTS.POST_SUBSCRIBE_LOGIN

      return
    }
    if (currentComponent.value === TOOLTIP_COMPONENTS.POST_SUBSCRIBE) {
      currentComponent.value = undefined
      return
    }

    await request()
    propagateAnalyticsEvent(post, ANALYTICS_EVENT_POST_SUBSCRIBE)
    currentComponent.value = TOOLTIP_COMPONENTS.POST_SUBSCRIBE
    if (statusCode.value === StatusCodes.OK) {
      alreadyPerformed.value = true
    } else if (statusCode.value === StatusCodes.CREATED) {
      alreadyPerformed.value = false
    }
  }
  return {
    isSubscribing,
    subscribePost,
  }
}

export function useSubscribeTopic(topicId: number) {
  const { subscribeTopic: subscribeTopicApi } = useSubscribersApi()
  const subscribeTopicText: Ref<string> = ref('post.actions.subscribeTopic')
  const {
    request,
    loading: isTopicSubscribing,
    statusCode,
  } = subscribeTopicApi(
    topicId,
    new ApiTimeout(TIMEOUT_DOMAIN_SUBSCRIBE_TOPIC + topicId)
  )

  const subscribeTopic = async () => {
    await request()
    if (statusCode.value === StatusCodes.OK) {
      subscribeTopicText.value = 'post.actionResults.topicAlreadySubscribed'
    } else if (statusCode.value === StatusCodes.CREATED) {
      subscribeTopicText.value = 'post.actionResults.topicSubscribed'
    }
  }

  return {
    subscribeTopicText,
    isTopicSubscribing,
    subscribeTopic,
  }
}

export function useSubscribeUser(userId: number, post?: Post) {
  const { subscribeUser: subscribeUserApi } = useSubscribersApi()
  const { propagateAnalyticsEvent } = useAnalyticsManager()
  const userAlreadySubscribed: Ref<boolean> = ref(false)
  const userSubscribePerformed: Ref<boolean> = ref(false)
  const {
    request,
    loading: isSubscribingUser,
    response,
    statusCode,
  } = subscribeUserApi(userId)

  const subscribeUser = async () => {
    if (typeof post !== 'undefined') {
      propagateAnalyticsEvent(post, ANALYTICS_EVENT_POST_USER_SUBSCRIBE)
    }

    await request()
    if (response.value) {
      currentUserSubscription.value = response.value
    }

    if (statusCode.value === StatusCodes.OK) {
      userAlreadySubscribed.value = true
      userSubscribePerformed.value = true
    } else if (statusCode.value === StatusCodes.CREATED) {
      userAlreadySubscribed.value = false
      userSubscribePerformed.value = true
    }
  }

  return {
    userAlreadySubscribed,
    userSubscribePerformed,
    isSubscribingUser,
    subscribeUser,
  }
}

export function useRemoveUserSubscription() {
  const { removeSubscription: removeSubscriptionApi } = useSubscribersApi()

  const isRemovingUserSubscription = ref<boolean>(false)

  const removeUserSubscription = async () => {
    if (typeof currentUserSubscription.value === 'undefined') {
      return
    }
    const { request } = removeSubscriptionApi(
      currentUserSubscription.value.id ?? 0
    )

    isRemovingUserSubscription.value = true
    await request()
    isRemovingUserSubscription.value = false
    currentUserSubscription.value = undefined
  }

  return {
    isRemovingUserSubscription,
    removeUserSubscription,
  }
}
